import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import DeleteHoldButton from '../common/DeleteHoldButton';
import type {
  ProgramSlideTableBlockV1,
  ProgramSlideTableTextAlignV1,
  ProgramSlideTableVerticalAlignV1,
} from '../../types/programContentSlideCanvas';
import {
  normalizeSlideTextFontFamily,
  PROGRAM_SLIDE_TEXT_FONT_OPTIONS,
} from '../../types/programContentSlideCanvas';
import {
  clearTableCellContents,
  deleteTableColumns,
  deleteTableRows,
  getAnchorCellIdsInSelection,
  getSelectedColIndices,
  getSelectedRowIndices,
  getTableInsertColIndex,
  getTableInsertRowIndex,
  insertTableColumn,
  insertTableRow,
  mergeTableCells,
  patchTableCells,
  canMergeTableSelection,
  canSplitTableSelection,
  expandSelectionToAnchorBounds,
  splitTableCellsInSelection,
  TABLE_DEFAULT_FONT_SIZE_PX,
  type TableCellSplitDirection,
  type TableSelectionRect,
} from '../../utils/programSlideTableBlock';
import { resolveSidePanelInContainer } from '../../utils/slideToolbarPlacement';
import {
  TABLE_TEXT_ALIGN_ICON_PATHS,
  TABLE_VERTICAL_ALIGN_ICON_SRCS,
} from './evaluationPlanSheetTableEditIcons';

const TEXT_ALIGN_OPTIONS: { id: ProgramSlideTableTextAlignV1; label: string }[] = [
  { id: 'left', label: '왼쪽 정렬' },
  { id: 'center', label: '가운데 정렬' },
  { id: 'right', label: '오른쪽 정렬' },
];

const VERTICAL_ALIGN_OPTIONS: { id: ProgramSlideTableVerticalAlignV1; label: string }[] = [
  { id: 'top', label: '위쪽 정렬' },
  { id: 'middle', label: '세로 가운데 정렬' },
  { id: 'bottom', label: '하단 정렬' },
];

const TableEditIconButton: React.FC<{
  label: string;
  disabled?: boolean;
  active?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}> = ({ label, disabled, active, onClick, children }) => (
  <button
    type="button"
    className={'cp-evaluation-plan-sheet-table-edit-icon-btn' + (active ? ' is-on' : '')}
    disabled={disabled}
    onClick={onClick}
    aria-label={label}
    title={label}
  >
    {children}
  </button>
);

const TableEditTextButton: React.FC<{
  label: string;
  disabled?: boolean;
  onClick: () => void;
}> = ({ label, disabled, onClick }) => (
  <button
    type="button"
    className="cp-btn-secondary cp-evaluation-plan-sheet-table-edit-text-btn"
    disabled={disabled}
    onClick={onClick}
  >
    {label}
  </button>
);

function normalizeHexColor(hex: string | undefined, fallback: string): string {
  return hex?.match(/^#/) ? hex : fallback;
}

function SlideLockIcon({ locked }: { locked?: boolean }) {
  return (
    <img
      src={locked ? '/lock.svg' : '/unlock.svg'}
      alt=""
      aria-hidden
      className="cp-program-slide-canvas-lock-icon"
    />
  );
}

const EvaluationPlanSheetTableEditMiniModal: React.FC<{
  open: boolean;
  table: ProgramSlideTableBlockV1 | null;
  selection: TableSelectionRect | null;
  anchorRect: DOMRect | null;
  containerRect?: DOMRect | null;
  panelZIndex?: number;
  onClose: () => void;
  onPatchTable: (table: ProgramSlideTableBlockV1, selection?: TableSelectionRect | null) => void;
  onDeleteTable: () => void;
  onToast?: (message: string) => void;
}> = ({
  open,
  table,
  selection,
  anchorRect,
  containerRect,
  panelZIndex,
  onClose,
  onPatchTable,
  onDeleteTable,
  onToast,
}) => {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const deleteHoldTimerRef = useRef<number | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number }>({ top: 80, left: 80 });
  const [, setHoldingDelete] = useState(false);

  const clearDeleteHoldTimer = useCallback(() => {
    if (deleteHoldTimerRef.current != null) {
      window.clearTimeout(deleteHoldTimerRef.current);
      deleteHoldTimerRef.current = null;
    }
    setHoldingDelete(false);
  }, []);

  useEffect(() => {
    return () => clearDeleteHoldTimer();
  }, [clearDeleteHoldTimer]);

  useEffect(() => {
    clearDeleteHoldTimer();
  }, [clearDeleteHoldTimer, table?.id]);

  useLayoutEffect(() => {
    if (!open || !anchorRect) return;
    const panel = panelRef.current;
    const pw = panel?.offsetWidth ?? 320;
    const ph = panel?.offsetHeight ?? 360;
    const margin = 12;

    if (containerRect) {
      const resolved = resolveSidePanelInContainer({
        containerRect,
        anchorRect,
        panelWidth: pw,
        panelHeight: ph,
        margin,
      });
      setPos(resolved);
      return;
    }

    let left = anchorRect.right + margin;
    let top = anchorRect.top;
    if (left + pw > window.innerWidth - margin) {
      left = Math.max(margin, anchorRect.left - pw - margin);
    }
    if (top + ph > window.innerHeight - margin) {
      top = Math.max(margin, window.innerHeight - ph - margin);
    }
    top = Math.max(margin, top);
    left = Math.max(margin, left);
    setPos({ top, left });
  }, [open, anchorRect, containerRect, table?.id, selection]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open || !table) return null;

  const isLocked = !!table.locked;
  const hasSelection = !!selection;
  const selectionRows = selection ? getSelectedRowIndices(selection) : [];
  const selectionCols = selection ? getSelectedColIndices(selection) : [];
  const canMerge = !!selection && canMergeTableSelection(table, selection);
  const canSplitHorizontal = !!selection && canSplitTableSelection(table, selection, 'horizontal');
  const canSplitVertical = !!selection && canSplitTableSelection(table, selection, 'vertical');
  const selectedCellIds = selection ? getAnchorCellIdsInSelection(table, selection) : [];
  const firstSelectedCell = selectedCellIds.length
    ? table.cells.find((c) => c.id === selectedCellIds[0])
    : null;
  const textColorHex = normalizeHexColor(firstSelectedCell?.colorHex, '#111111');
  const backgroundColorHex = normalizeHexColor(firstSelectedCell?.backgroundColorHex, '#ffffff');
  const borderColorHex = normalizeHexColor(table.borderColorHex, '#333333');
  const fontSizePx = table.defaultFontSizePx ?? TABLE_DEFAULT_FONT_SIZE_PX;
  const fontFamily = normalizeSlideTextFontFamily(table.defaultFontFamily);
  const isBold = !!firstSelectedCell?.bold;
  const cellsDisabled = !selectedCellIds.length;

  const patchFontSizePx = (next: number) => {
    onPatchTable({
      ...table,
      defaultFontSizePx: Math.min(48, Math.max(8, Math.round(next) || TABLE_DEFAULT_FONT_SIZE_PX)),
    });
  };

  const toast = (msg: string) => onToast?.(msg);

  const runSplit = (direction: TableCellSplitDirection) => {
    if (!selection) return;
    const result = splitTableCellsInSelection(table, selection, direction);
    if (result.error) toast(result.error);
    else onPatchTable(result.table, result.selection);
  };

  const node = (
    <div
      ref={panelRef}
      className="cp-evaluation-plan-sheet-table-edit-mini-modal"
      style={{ top: pos.top, left: pos.left, ...(panelZIndex != null ? { zIndex: panelZIndex } : {}) }}
      role="dialog"
      aria-modal="true"
      aria-label="표 편집"
      onPointerDown={(e) => e.stopPropagation()}
    >
      <div className="cp-evaluation-plan-sheet-table-edit-section">
        <div className="cp-evaluation-plan-sheet-table-edit-row cp-evaluation-plan-sheet-table-edit-row--equal">
          <TableEditTextButton
            label="위쪽 행 추가"
            disabled={isLocked || !hasSelection}
            onClick={() =>
              onPatchTable(insertTableRow(table, getTableInsertRowIndex(selection!, 'above'), 'above'))
            }
          />
          <TableEditTextButton
            label="아래쪽 행 추가"
            disabled={isLocked || !hasSelection}
            onClick={() =>
              onPatchTable(insertTableRow(table, getTableInsertRowIndex(selection!, 'below'), 'below'))
            }
          />
          <TableEditTextButton
            label="행 삭제"
            disabled={isLocked || !hasSelection || table.rowCount - selectionRows.length < 1}
            onClick={() => onPatchTable(deleteTableRows(table, selectionRows))}
          />
        </div>
        <div className="cp-evaluation-plan-sheet-table-edit-row cp-evaluation-plan-sheet-table-edit-row--equal">
          <TableEditTextButton
            label="왼쪽 열 추가"
            disabled={isLocked || !hasSelection}
            onClick={() =>
              onPatchTable(insertTableColumn(table, getTableInsertColIndex(selection!, 'left'), 'left'))
            }
          />
          <TableEditTextButton
            label="오른쪽 열 추가"
            disabled={isLocked || !hasSelection}
            onClick={() =>
              onPatchTable(insertTableColumn(table, getTableInsertColIndex(selection!, 'right'), 'right'))
            }
          />
          <TableEditTextButton
            label="열 삭제"
            disabled={isLocked || !hasSelection || table.colCount - selectionCols.length < 1}
            onClick={() => onPatchTable(deleteTableColumns(table, selectionCols))}
          />
        </div>
      </div>

      <div className="cp-evaluation-plan-sheet-table-edit-section">
        <div className="cp-evaluation-plan-sheet-table-edit-row cp-evaluation-plan-sheet-table-edit-row--equal cp-evaluation-plan-sheet-table-edit-row--equal-4 cp-evaluation-plan-sheet-table-edit-row--merge">
          <TableEditTextButton
            label="셀 병합"
            disabled={isLocked || !canMerge}
            onClick={() => {
              if (!selection) return;
              const expanded = expandSelectionToAnchorBounds(table, selection);
              const result = mergeTableCells(table, expanded);
              if (result.error) toast(result.error);
              else onPatchTable(result.table, result.selection);
            }}
          />
          <TableEditTextButton
            label="가로 나누기"
            disabled={isLocked || !canSplitHorizontal}
            onClick={() => runSplit('horizontal')}
          />
          <TableEditTextButton
            label="세로 나누기"
            disabled={isLocked || !canSplitVertical}
            onClick={() => runSplit('vertical')}
          />
          <TableEditTextButton
            label="내용 지우기"
            disabled={isLocked || !selectedCellIds.length}
            onClick={() => onPatchTable(clearTableCellContents(table, selectedCellIds))}
          />
        </div>
      </div>

      <div className="cp-evaluation-plan-sheet-table-edit-section">
        <div className="cp-evaluation-plan-sheet-table-edit-type-row">
          <label className="cp-program-slide-canvas-tl">
            크기
            <input
              type="number"
              min={8}
              disabled={isLocked}
              max={48}
              value={fontSizePx}
              onChange={(e) => patchFontSizePx(Number(e.target.value) || TABLE_DEFAULT_FONT_SIZE_PX)}
              aria-label="텍스트 크기"
            />
            <div className="cp-program-slide-canvas-fontstep-group" role="group" aria-label="텍스트 크기 조절">
              <button
                type="button"
                className="cp-program-slide-canvas-fontstep"
                onClick={() => patchFontSizePx(fontSizePx - 1)}
                disabled={isLocked}
                aria-label="텍스트 크기 줄이기"
                title="텍스트 크기 줄이기"
              >
                <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                  <path d="M5 11h14v2H5v-2z" />
                </svg>
              </button>
              <button
                type="button"
                className="cp-program-slide-canvas-fontstep"
                onClick={() => patchFontSizePx(fontSizePx + 1)}
                disabled={isLocked}
                aria-label="텍스트 크기 키우기"
                title="텍스트 크기 키우기"
              >
                <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                  <path d="M11 5h2v14h-2V5zM5 11h14v2H5v-2z" />
                </svg>
              </button>
            </div>
          </label>
          <label className="cp-program-slide-canvas-tl cp-program-slide-canvas-font-family-field">
            글꼴
            <select
              value={fontFamily}
              disabled={isLocked}
              onChange={(e) =>
                onPatchTable({
                  ...table,
                  defaultFontFamily: normalizeSlideTextFontFamily(e.target.value),
                })
              }
              aria-label="글꼴"
            >
              {PROGRAM_SLIDE_TEXT_FONT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label className="cp-program-slide-canvas-tl">
            텍스트 색상
            <span className="cp-program-slide-canvas-color-wrap">
              <span
                className="cp-program-slide-canvas-color-swatch"
                style={{ backgroundColor: textColorHex }}
                aria-hidden
              />
              <input
                type="color"
                value={textColorHex}
                disabled={isLocked || cellsDisabled}
                onChange={(e) => onPatchTable(patchTableCells(table, selectedCellIds, { colorHex: e.target.value }))}
                aria-label="텍스트 색상"
              />
            </span>
          </label>
          <button
            type="button"
            className={'cp-program-slide-canvas-bold' + (isBold ? ' is-on' : '')}
            disabled={isLocked || cellsDisabled}
            onClick={() => onPatchTable(patchTableCells(table, selectedCellIds, { bold: !isBold }))}
            aria-label="굵게"
            title="굵게"
          >
            B
          </button>
          <div
            className="cp-program-slide-canvas-align-group cp-evaluation-plan-sheet-table-edit-align-all"
            role="group"
            aria-label="텍스트 정렬"
          >
            {TEXT_ALIGN_OPTIONS.map((opt) => (
              <TableEditIconButton
                key={opt.id}
                label={opt.label}
                active={firstSelectedCell?.textAlign === opt.id}
                disabled={isLocked || cellsDisabled}
                onClick={() => onPatchTable(patchTableCells(table, selectedCellIds, { textAlign: opt.id }))}
              >
                <svg viewBox="0 0 24 24" width={18} height={18} aria-hidden focusable="false">
                  {TABLE_TEXT_ALIGN_ICON_PATHS[opt.id]}
                </svg>
              </TableEditIconButton>
            ))}
            {VERTICAL_ALIGN_OPTIONS.map((opt) => (
              <TableEditIconButton
                key={opt.id}
                label={opt.label}
                active={(firstSelectedCell?.verticalAlign ?? 'middle') === opt.id}
                disabled={isLocked || cellsDisabled}
                onClick={() =>
                  onPatchTable(patchTableCells(table, selectedCellIds, { verticalAlign: opt.id }))
                }
              >
                <img src={TABLE_VERTICAL_ALIGN_ICON_SRCS[opt.id]} alt="" width={18} height={18} aria-hidden />
              </TableEditIconButton>
            ))}
          </div>
        </div>
      </div>

      <div className="cp-evaluation-plan-sheet-table-edit-section">
        <div className="cp-evaluation-plan-sheet-table-edit-style-row">
          <label className="cp-program-slide-canvas-tl">
            셀 배경 색상
            <span className="cp-program-slide-canvas-color-wrap">
              <span
                className="cp-program-slide-canvas-color-swatch"
                style={{ backgroundColor: backgroundColorHex }}
                aria-hidden
              />
              <input
                type="color"
                value={backgroundColorHex}
                disabled={isLocked || cellsDisabled}
                onChange={(e) =>
                  onPatchTable(patchTableCells(table, selectedCellIds, { backgroundColorHex: e.target.value }))
                }
                aria-label="셀 배경 색상"
              />
            </span>
          </label>
          <label className="cp-program-slide-canvas-tl">
            테두리 색상
            <span className="cp-program-slide-canvas-color-wrap">
              <span
                className="cp-program-slide-canvas-color-swatch"
                style={{ backgroundColor: borderColorHex }}
                aria-hidden
              />
              <input
                type="color"
                value={borderColorHex}
                disabled={isLocked}
                onChange={(e) => onPatchTable({ ...table, borderColorHex: e.target.value })}
                aria-label="테두리 색상"
              />
            </span>
          </label>
          <label className="cp-program-slide-canvas-tl">
            테두리 두께
            <input
              type="number"
              min={0}
              max={8}
              disabled={isLocked}
              value={table.borderWidthPx}
              onChange={(e) =>
                onPatchTable({
                  ...table,
                  borderWidthPx: Math.min(8, Math.max(0, Math.round(Number(e.target.value) || 0))),
                })
              }
              aria-label="테두리 두께"
            />
          </label>
          <button
            type="button"
            className={'cp-program-slide-canvas-lock' + (isLocked ? ' is-on' : '')}
            onClick={() =>
              onPatchTable({ ...table, locked: isLocked ? undefined : true })
            }
            aria-label={isLocked ? '잠금 해제' : '잠금'}
            title={isLocked ? '잠금 해제' : '잠금'}
          >
            <SlideLockIcon locked={isLocked} />
          </button>
          <DeleteHoldButton
            className="cp-program-slide-canvas-trash"
            holdMs={1000}
            disabled={isLocked}
            disabledTitle="잠금 상태에서는 삭제할 수 없습니다"
            disabledAriaLabel="표 삭제 (잠금됨)"
            title="표 삭제 (짧게: 확인 후 삭제 · 길게: 바로 삭제)"
            ariaLabel="표 삭제"
            confirmTitle="표를 삭제할까요?"
            confirmDescription="삭제하면 되돌릴 수 없습니다."
            onDelete={onDeleteTable}
          >
            <img src="/Trash.svg" alt="" aria-hidden className="cp-program-slide-canvas-trash-icon" />
          </DeleteHoldButton>
        </div>
      </div>
    </div>
  );

  return createPortal(node, document.body);
};

export default EvaluationPlanSheetTableEditMiniModal;
